import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { frFR } from '@mui/material/locale';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ClientsListPage from './pages/ClientsListPage';
import ClientFormPage from './pages/ClientFormPage';
import ClientDetailPage from './pages/ClientDetailPage';
import DevisListPage from './pages/DevisListPage';
import DevisFormPage from './pages/DevisFormPage';
import DevisDetailPage from './pages/DevisDetailPage';
import FactureFormPage from './pages/FactureFormPage';
import FactureListPage from './pages/FactureListPage';
import FactureDetailPage from './pages/FactureDetailPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import PublicPaymentSuccessPage from './pages/PublicPaymentSuccessPage';
import PublicPaymentCancelPage from './pages/PublicPaymentCancelPage';
import PhotosChantierPage from './pages/PhotosChantierPage';
import ProfilePage from './pages/ProfilePage';
import LegalInfoPage from './pages/LegalInfoPage';
import DocumentCustomizationPage from './pages/DocumentCustomizationPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import WelcomePage from './pages/WelcomePage';
import ExpensesPage from './pages/ExpensesPage';
import ProjetsListPage from './pages/ProjetsListPage';
import ProjetFormPage from './pages/ProjetFormPage';
import ProjetDetailPage from './pages/ProjetDetailPage';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { NotificationContainer } from './components/notifications';

// Stores
import { useAuthStore } from './stores/useAuthStore';
import { useClientStore } from './stores/useClientStore';
import { useProjetStore } from './stores/useProjetStore';
import { useQuoteStore } from './stores/useQuoteStore';
import { useFactureStore } from './stores/useFactureStore';

// Context
import { NotificationProvider } from './contexts/NotificationContext';

// Thème Material-UI personnalisé - Gris Anthracite Pro
const theme = createTheme(
  {
    palette: {
      primary: {
        main: '#374151', // Gris anthracite
        light: '#4B5563', // Gris moyen
        dark: '#1F2937', // Gris très foncé
      },
      secondary: {
        main: '#F59E0B', // Orange accent chantier
        light: '#FBBF24', // Orange clair
        dark: '#D97706', // Orange foncé
      },
      success: {
        main: '#10B981', // Vert moderne
      },
      error: {
        main: '#EF4444', // Rouge moderne
      },
      warning: {
        main: '#F59E0B', // Orange (cohérent avec secondary)
      },
      info: {
        main: '#3B82F6', // Bleu information
      },
      background: {
        default: '#F9FAFB', // Gris très clair
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h4: {
        fontWeight: 700,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
          sizeLarge: {
            padding: '12px 24px',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
    },
  },
  frFR
);

function App() {
  const loadAuth = useAuthStore((state) => state.loadFromStorage);
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const loadClients = useClientStore((state) => state.loadClients);
  const loadProjets = useProjetStore((state) => state.loadProjets);
  const loadDevis = useQuoteStore((state) => state.loadDevis);
  const loadFactures = useFactureStore((state) => state.loadFactures);

  // Charger l'auth depuis le localStorage au démarrage
  useEffect(() => {
    loadAuth();
  }, [loadAuth]);

  // Écouter l'événement d'expiration de session depuis l'intercepteur API
  useEffect(() => {
    const handleAuthExpired = async () => {
      console.log('🔒 Événement auth:expired reçu - déconnexion...');
      await logout();
      // Rediriger vers la page de login
      window.location.replace('/login');
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [logout]);

  // Charger les données depuis l'API si l'utilisateur est authentifié
  // On attend que le chargement initial soit terminé pour éviter de charger avec un token expiré
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      console.log('🔄 Chargement des données pour l\'utilisateur authentifié...');
      loadClients();
      loadProjets();
      loadDevis();
      loadFactures();
    }
  }, [isAuthenticated, isLoading, loadClients, loadProjets, loadDevis, loadFactures]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <BrowserRouter>
          <NotificationContainer />
          <Routes>
          {/* Page d'accueil publique */}
          <Route path="/" element={<LandingPage />} />

          {/* Routes publiques */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Page de bienvenue (après inscription) */}
          <Route path="/welcome" element={<WelcomePage />} />

          {/* Routes publiques pour les paiements clients (sans authentification) */}
          <Route path="/public/payment-success" element={<PublicPaymentSuccessPage />} />
          <Route path="/public/payment-cancel" element={<PublicPaymentCancelPage />} />

          {/* Routes protégées avec Layout */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Routes Clients */}
            <Route path="/clients">
              <Route index element={<ClientsListPage />} />
              <Route path="new" element={<ClientFormPage />} />
              <Route path="edit/:id" element={<ClientFormPage />} />
              <Route path=":id" element={<ClientDetailPage />} />
            </Route>

            {/* Routes Projets */}
            <Route path="/projets">
              <Route index element={<ProjetsListPage />} />
              <Route path="new" element={<ProjetFormPage />} />
              <Route path="edit/:id" element={<ProjetFormPage />} />
              <Route path=":id" element={<ProjetDetailPage />} />
            </Route>

            {/* Routes Devis */}
            <Route path="/devis">
              <Route index element={<DevisListPage />} />
              <Route path="new" element={<DevisFormPage />} />
              <Route path="edit/:id" element={<DevisFormPage />} />
              <Route path=":id" element={<DevisDetailPage />} />
            </Route>

            {/* Routes Factures */}
            <Route path="/factures">
              <Route index element={<FactureListPage />} />
              <Route path="new/:devisId" element={<FactureFormPage />} />
              <Route path=":id" element={<FactureDetailPage />} />
              <Route path="payment-success" element={<PaymentSuccessPage />} />
              <Route path="payment-cancel" element={<PaymentCancelPage />} />
            </Route>

            {/* Routes Photos Chantier */}
            <Route path="/photos">
              <Route path=":devisId" element={<PhotosChantierPage />} />
              <Route path="projet/:projetId" element={<PhotosChantierPage />} />
            </Route>

            {/* Routes Dépenses */}
            <Route path="/expenses" element={<ExpensesPage />} />

            {/* Route Profil */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />

            {/* Route Informations Légales */}
            <Route path="/legal-info" element={<LegalInfoPage />} />

            {/* Route Personnalisation Documents */}
            <Route path="/customization" element={<DocumentCustomizationPage />} />

            {/* Routes Abonnement */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
          </Route>

          {/* Redirection par défaut */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
